import React from 'react';
import { formatCurrency, formatEmailCount } from '../../utils/pricingCalculator';
import { PricingSlab } from '../../types';

interface PricingTableProps {
  currency: 'INR' | 'USD';
  slabs: PricingSlab[];
}

const PricingTable: React.FC<PricingTableProps> = ({ currency, slabs }) => {
  const formatSlabRange = (minEmails: number, maxEmails: number | null): string => {
    if (maxEmails === null) {
      return `${formatEmailCount(minEmails)}+`;
    }
    return `${formatEmailCount(minEmails)} - ${formatEmailCount(maxEmails)}`;
  };

  const getTierColor = (index: number): string => {
    const colors = [
      'tier-starter', 'tier-growth', 'tier-business', 'tier-scale',
      'tier-enterprise', 'tier-volume', 'tier-premium', 'tier-ultimate',
      'tier-mega', 'tier-unlimited'
    ];
    return colors[index] || 'tier-default';
  };

  return (
    <div className="pricing-table-container">
      <div className="pricing-table-header">
        <h3>Email Plan Pricing</h3>
        <p>Fixed monthly rates based on your email volume</p>
      </div>

      <div className="pricing-table">
        <div className="pricing-table-scroll">
          <table role="table" aria-label={`Email pricing plans in ${currency}`}>
            <thead>
              <tr>
                <th scope="col">Email Volume</th>
                <th scope="col">Rate per Lakh</th>
                <th scope="col">Plan Features</th>
              </tr>
            </thead>
            <tbody>
              {slabs.map((slab, index) => {
                return (
                  <tr key={index} className={getTierColor(index)}>
                    <td data-label="Email Volume">
                      <div className="volume-cell">
                        <span className="volume-range">
                          <strong>{slab.label}</strong>
                        </span>
                        <span className="volume-numbers">
                          {formatSlabRange(slab.minEmails, slab.maxEmails)}
                        </span>
                      </div>
                    </td>
                    <td data-label="Rate per Lakh">
                      <span className="monthly-price">
                        {formatCurrency(slab.pricePerLakh, currency)}
                      </span>
                      <span className="price-note">/lakh</span>
                    </td>
                    <td data-label="Plan Features">
                      <div className="features-list">
                        {index === 0 && (
                          <>
                            <span>✓ Email campaigns</span>
                            <span>✓ Basic templates</span>
                            <span>✓ Standard support</span>
                          </>
                        )}
                        {index >= 1 && index <= 3 && (
                          <>
                            <span>✓ All basic features</span>
                            <span>✓ Advanced analytics</span>
                            <span>✓ A/B testing</span>
                          </>
                        )}
                        {index >= 4 && index <= 6 && (
                          <>
                            <span>✓ All growth features</span>
                            <span>✓ Priority support</span>
                            <span>✓ Custom integrations</span>
                          </>
                        )}
                        {index >= 7 && (
                          <>
                            <span>✓ Enterprise features</span>
                            <span>✓ Dedicated support</span>
                            <span>✓ Custom solutions</span>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        <div className="pricing-note">
          <p>All plans include unlimited users, advanced automation, detailed reporting, and 24/7 customer support.</p>
        </div>
      </div>
    </div>
  );
};

export default PricingTable;